import { Injectable } from "@angular/core";
import {
	DataProtectionConfigService,
	FraudPreventionConfigService,
	GroupIBFraudPreventionService,
} from "@obg/ngx.common";
import { CoreHttpClient, Market, ProductType } from "@obg/ngx.core";
import { Observable } from "rxjs";

import { RegistrationConfigService } from "../registration.config";
import { RegistrationFormFlow, RegistrationResponse } from "../registration.model";
import { RegistrationService } from "../registration.service";

import { RegistrationWithPersonalNumberConfigService } from "./registration-with-personal-number.config";
import {
	PersonalNumberVerificationResponse,
	RegistrationWithPersonalNumberCompletePayload,
	RegistrationWithPersonalNumberCompleteRequest,
	RegistrationWithPersonalNumberSimplePayload,
	RegistrationWithPersonalNumberSimpleRequest,
} from "./registration-with-personal-number.model";

@Injectable({
	providedIn: "root",
})
export class RegistrationWithPersonalNumberService {
	constructor(
		private httpClient: CoreHttpClient,
		private registrationConfig: RegistrationConfigService,
		private registrationWithPersonalNumberConfig: RegistrationWithPersonalNumberConfigService,
		private fraudPreventionConfigService: FraudPreventionConfigService,
		private groupIBFraudPreventionService: GroupIBFraudPreventionService,
		private dataProtectionConfig: DataProtectionConfigService,
		private registrationService: RegistrationService
	) {}

	registerCompleteFlow(
		payload: RegistrationWithPersonalNumberCompletePayload,
		personalNumber: string,
		market: Market,
		product = ProductType.Common
	) {
		return this.httpClient.post<RegistrationResponse>({
			url: this.registrationConfig.getRegisterEndpoint(),
			data: this.buildCompleteRegisterRequest(personalNumber, market, payload, product),
		});
	}

	registerSimpleFlow(
		payload: RegistrationWithPersonalNumberSimplePayload,
		personalNumber: string,
		market: Market,
		product = ProductType.Common
	) {
		return this.httpClient.post<RegistrationResponse>({
			url: this.registrationConfig.getRegisterEndpoint(),
			data: this.buildSimpleRegisterRequest(personalNumber, market, payload, product),
		});
	}

	verifyPersonalNumber(personalNumber: string): Observable<PersonalNumberVerificationResponse> {
		return this.httpClient.get<PersonalNumberVerificationResponse>({
			url: this.registrationWithPersonalNumberConfig.getPersonalNumberVerificationEndpoint(),
			searchParams: { personalNumber },
		});
	}

	private buildSimpleRegisterRequest(
		personalNumber: string,
		market: Market,
		payload: RegistrationWithPersonalNumberSimplePayload | RegistrationWithPersonalNumberCompletePayload,
		registeredFromProduct: ProductType
	): RegistrationWithPersonalNumberSimpleRequest {
		const { iovationBlackBox, legal, credential, phoneNumber, phoneExtension } = payload;

		let consents = payload.consents;
		let marketingOffers = payload.marketingOffers;
		if (!this.dataProtectionConfig.getMarketingConsentDisabled() && consents) {
			consents = {
				brandMarketing: this.registrationConfig.getMarketingConsentsOptOutCountries().includes("SV")
					? !consents.brandMarketing
					: consents.brandMarketing,
			};

			marketingOffers = {
				acceptEmailOffers: consents.brandMarketing,
				acceptSmsOffers: consents.brandMarketing,
				acceptTelesalesOffers: consents.brandMarketing,
			};
		}

		return {
			language: market.dataLanguageCode ?? market.languageCode,
			timeZone: "utc",
			iovationBlackBox: this.fraudPreventionConfigService.getEnabledInRegistration() ? iovationBlackBox : "",
			fraudToken: this.groupIBFraudPreventionService.getSessionId(),
			person: {
				personalNumber,
			},
			legal,
			credential: {
				...credential,
				username: credential.username || credential.email,
			},
			subscriptions: {
				...marketingOffers,
				email: credential.email,
				phoneExtension: phoneExtension.replace(/(^\+?)/, "+"),
				phoneNumber,
			},
			affiliateMetadata: this.registrationService.getAffiliateMetaData(),
			consents,
			flow: RegistrationFormFlow.Simple,
			productMetadata: {
				registeredFromProduct,
			},
		};
	}

	private buildCompleteRegisterRequest(
		personalNumber: string,
		market: Market,
		payload: RegistrationWithPersonalNumberCompletePayload,
		registeredFromProduct: ProductType
	): RegistrationWithPersonalNumberCompleteRequest {
		const simpleRequest = this.buildSimpleRegisterRequest(personalNumber, market, payload, registeredFromProduct);
		return {
			...simpleRequest,
			person: {
				...payload.person,
				personalNumber,
			},
			address: payload.address,
			flow: RegistrationFormFlow.Complete,
		};
	}
}
